import { useEffect, useRef, useState } from "react";
import { createSocket } from "./socket/create.socket";
import type { Socket } from "socket.io-client";
import type { DeviceDetails } from "./interface/device.details";
import { apiRoutes } from "./utils/apiRoutes";
import { dcmmApi } from "./utils/axiosConfig";
import { DisplayType } from "@industronics/fids-utils";

import FlightCheckInPage from "./pages/FlightCheckInPage";
import FlightBoardingPage from "./pages/FlightBoardingPage";
import BaggageCarouselPage from "./pages/BaggageCarouselPage";
import CheckInRowPage from "./pages/CheckInRowPage";
import ConnectDevicePage from "./pages/ConnectDevicePage";
import ConfigureDevicePage from "./pages/ConfigureDevicePage";

import { SocketProvider } from "./context/SocketContext";
import { DeviceConfigService } from "./services/deviceConfigService";
import airplaneWing from "./assets/airplaneWing.jpg";
import mahbBg from "./assets/MAHB_BG.jpg";
import LocateDeviceOverlay from "./components/LocateDeviceOverlay";
import html2canvas from "html2canvas";

// ── On-prem: device is on LAN — navigator.onLine is irrelevant ───────────────
const IS_ON_PREM = process.env.REACT_APP_DEPLOYMENT_MODE === "on-prem";
const BLACKOUT_DELAY_MS = 15 * 60_000;
console.log("REACT_APP_DEPLOYMENT_MODE", process.env.REACT_APP_DEPLOYMENT_MODE);
console.log("IS_ON_PREM", IS_ON_PREM);

const isCorsOrNetworkError = (error: any): boolean =>
    !error.response ||
    error.code === "ERR_NETWORK" ||
    error.code === "ECONNABORTED" ||
    error.message?.toLowerCase().includes("network error") ||
    error.message?.toLowerCase().includes("cors");

const isRetryableError = (error: any): boolean => {
    if (isCorsOrNetworkError(error)) return true;
    const status = error.response?.status;
    return status >= 500 || status === 429 || status === 408;
};

const App = () => {
    const socketRef = useRef<Socket | null>(null);
    const [socket, setSocket] = useState<Socket | null>(null);

    const [deviceData, setDeviceData] = useState<DeviceDetails>();
    const [loadingText, setLoadingText] = useState<string>("");

    // On-prem: always treat as online — LAN doesn't depend on internet
    const [isOnline, setIsOnline] = useState<boolean>(
        IS_ON_PREM ? true : (typeof navigator !== "undefined" ? navigator.onLine : true),
    );

    const [isConfigured, setIsConfigured] = useState<boolean>(false);
    const [isAuthenticating, setIsAuthenticating] = useState<boolean>(true);
    const [authError, setAuthError] = useState<string>("");
    const [isSocketConnected, setIsSocketConnected] = useState<boolean>(false);
    const [isBlackout, setIsBlackout] = useState<boolean>(false);
    const blackoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isLocateVisible, setIsLocateVisible] = useState(false);
    const [locateDeviceCode, setLocateDeviceCode] = useState<string>("");

    // ── Black screen after prolonged socket disconnect ────────────────────────
    useEffect(() => {
        if (!isSocketConnected) {
            blackoutTimerRef.current = setTimeout(() => {
                console.warn("[BLACKOUT] Socket disconnected too long — blanking screen");
                setIsBlackout(true);
            }, BLACKOUT_DELAY_MS);
        } else {
            if (blackoutTimerRef.current) {
                clearTimeout(blackoutTimerRef.current);
                blackoutTimerRef.current = null;
            }
            setIsBlackout(false);
        }

        return () => {
            if (blackoutTimerRef.current) clearTimeout(blackoutTimerRef.current);
        };
    }, [isSocketConnected]);

    // ── Network watcher — skipped entirely on on-prem ─────────────────────────
    useEffect(() => {
        if (IS_ON_PREM) {
            return;
        }

        const handleOnline = () => {
            console.log("[NET] Back online");
            setIsOnline(true);
            setLoadingText("");
            if (isConfigured && !deviceData) handleSearchDevice();
        };
        const handleOffline = () => {
            console.log("[NET] Went offline");
            setIsOnline(false);
            setLoadingText("No internet connection");
            socketRef.current?.disconnect();
        };

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, [isConfigured, deviceData]);

    // ── Boot ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        checkAuthentication();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkAuthentication = async () => {
        setIsAuthenticating(true);
        setAuthError("");

        // On-prem gets more attempts — server may be booting alongside device
        const MAX_AUTH_ATTEMPTS = IS_ON_PREM ? 10 : 3;
        const AUTH_RETRY_DELAYS = IS_ON_PREM
            ? [2000, 5000, 10000, 15000, 20000, 20000, 20000, 20000, 20000, 20000]
            : [2000, 5000, 10000];

        try {
            // isConfigured():
            //   On-prem → always true (IP is the identity, no manual config step)
            //   Cloud   → true only after registrationRequest stored credentials
            if (!DeviceConfigService.isConfigured()) {
                setIsConfigured(false);
                setIsAuthenticating(false);
                return;
            }

            for (let attempt = 0; attempt < MAX_AUTH_ATTEMPTS; attempt++) {
                try {
                    if (attempt > 0) {
                        console.log(`🔄 Retrying auth (${attempt + 1}/${MAX_AUTH_ATTEMPTS})...`);
                    } else {
                        // login() handles branching internally:
                        //   On-prem → POST /device-auth/loginByIp  (no body)
                        //   Cloud   → POST /device-auth/login      (apiKey + secretKey)
                        console.log(IS_ON_PREM ? "🏢 Authenticating via IP..." : "☁️ Logging in with stored credentials...");
                    }

                    // No inner retries — this loop already retries with its own delays
                    await DeviceConfigService.login(undefined, undefined, { maxRetries: 0 });
                    setIsConfigured(true);
                    await handleSearchDevice();
                    return;
                } catch (error: any) {
                    // 401/403 are terminal — don't retry, show appropriate message
                    if (error.response?.status === 401 || error.response?.status === 403) {
                        const msg = IS_ON_PREM
                            ? (error.response.status === 403
                                ? "Device is inactive or faulty. Please contact administrator."
                                : "Device IP not recognised. Please contact administrator.")
                            : (error?.message || "Authentication failed. Please contact administrator.");

                        setAuthError(msg);
                        setIsConfigured(true); // render error screen, not configure screen
                        setIsAuthenticating(false);
                        return;
                    }

                    const isLastAttempt = attempt === MAX_AUTH_ATTEMPTS - 1;
                    if (!isRetryableError(error) || isLastAttempt) {
                        setAuthError(error?.message || "Authentication failed");
                        setIsConfigured(true);
                        setIsAuthenticating(false);
                        return;
                    }

                    const delay = AUTH_RETRY_DELAYS[attempt];
                    setLoadingText(`Server not ready, retrying in ${Math.round(delay / 1000)}s...`);
                    await new Promise(r => setTimeout(r, delay));
                }
            }
        } catch (error: any) {
            setAuthError(error?.message || "Authentication failed");
            setIsConfigured(true);
        } finally {
            setIsAuthenticating(false);
        }
    };

    // ── Auto-refresh token every 23 h ─────────────────────────────────────────
    // login() handles on-prem (loginByIp) vs cloud (apiKey+secretKey) internally
    useEffect(() => {
        if (!isConfigured) return;
        const id = setInterval(async () => {
            try {
                await DeviceConfigService.reLogin();
            } catch (e) {
                console.error("Token refresh failed:", e);
            }
        }, 23 * 60 * 60 * 1000);
        return () => clearInterval(id);
    }, [isConfigured]);

    // ── Create socket ONLY after login AND deviceData are ready ───────────────
    // 1) create socket once when app is ready
    useEffect(() => {
        if (!isConfigured || !deviceData) return;
        if (socketRef.current) return;

        const s = createSocket();
        socketRef.current = s;
        setSocket(s);

        return () => {
            s.disconnect();
            socketRef.current = null;
            setSocket(null);
        };
    }, [isConfigured, deviceData]);

    // 2) bind socket listeners separately
    useEffect(() => {
        if (!socket || !deviceData) return;

        const handleConnect = () => {
            console.log("✅ Socket connected:", socket.id);
            setIsSocketConnected(true);
            if (deviceData._id) {
                socket.emit("device:join", { deviceId: deviceData._id });
            }
        };

        const handleWelcome = (msg: string) => {
            console.log("📨 Server:", msg);
        };

        const handleRoomJoined = (d: any) => {
            console.log("✅ Room joined:", d);
        };

        const handleDeviceUpdate = async () => {
            console.log("📡 device:update — refreshing device data");
            await handleSearchDevice();
        };

        const handleScreenshotRequest = async () => {
            console.log("📸 screenshot request");
            await uploadDeviceScreenshot();
        };

        const handleReloadApp = () => {
            console.log("🔄 reload:app");
            window.location.reload();
        };

        const handleDeviceLocate = (payload?: { deviceCode?: string }) => {
            console.log("FRONTEND device.locate received", payload);
            setLocateDeviceCode(payload?.deviceCode || deviceData?.deviceCode || "");
            setIsLocateVisible(true);
        };

        const handleDisconnect = (r: string) => {
            console.warn("❌ Disconnected:", r);
            setIsSocketConnected(false);
        };

        const handleReconnect = (n: number) => {
            console.log("🔄 Reconnected after", n, "attempts");
            setIsSocketConnected(true);
        };

        const handleReconnectAttempt = (n: number) => {
            console.log("🔄 Reconnect attempt #", n);
        };

        const handleReconnectFailed = () => {
            console.error("❌ All reconnects failed");
            setIsSocketConnected(false);
        };

        const handleConnectError = (e: Error) => {
            console.error("❌ Connect error:", e.message);
            setIsSocketConnected(false);
        };

        socket.on("connect", handleConnect);
        socket.on("welcome", handleWelcome);
        socket.on("room:joined", handleRoomJoined);
        socket.on("device:update", handleDeviceUpdate);
        socket.on("device.request.screenshot", handleScreenshotRequest);
        socket.on("reload:app", handleReloadApp);
        socket.on("device.locate", handleDeviceLocate);
        socket.on("disconnect", handleDisconnect);
        socket.on("reconnect", handleReconnect);
        socket.on("reconnect_attempt", handleReconnectAttempt);
        socket.on("reconnect_failed", handleReconnectFailed);
        socket.on("connect_error", handleConnectError);

        // if socket is already connected when this effect runs
        if (socket.connected) {
            handleConnect();
        }

        return () => {
            socket.off("connect", handleConnect);
            socket.off("welcome", handleWelcome);
            socket.off("room:joined", handleRoomJoined);
            socket.off("device:update", handleDeviceUpdate);
            socket.off("device.request.screenshot", handleScreenshotRequest);
            socket.off("reload:app", handleReloadApp);
            socket.off("device.locate", handleDeviceLocate);
            socket.off("disconnect", handleDisconnect);
            socket.off("reconnect", handleReconnect);
            socket.off("reconnect_attempt", handleReconnectAttempt);
            socket.off("reconnect_failed", handleReconnectFailed);
            socket.off("connect_error", handleConnectError);
        };
    }, [socket, deviceData]);

    // ── Connect/disconnect socket on network change — cloud only ─────────────
    useEffect(() => {
        if (IS_ON_PREM) return; // LAN — let socket manage its own reconnects
        const s = socketRef.current;
        if (!s) return;
        isOnline ? s.connect() : s.disconnect();
    }, [isOnline]);

    // ── Fetch device data ─────────────────────────────────────────────────────
    const handleSearchDevice = async (isManualRetry = false) => {
        const FAST_RETRY_ATTEMPTS = 3;
        const FAST_RETRY_DELAYS = IS_ON_PREM ? [2000, 5000, 10000] : [1000, 2000, 3000];
        const CYCLE_BREAK_DELAY = IS_ON_PREM ? 15000 : 10000;
        const MAX_CYCLES = 50;

        let currentCycle = 0;
        let consecutiveFailures = 0;

        const attemptFetch = async (): Promise<boolean> => {
            try {
                // Cloud only: check internet before attempting
                if (!IS_ON_PREM && !navigator.onLine) {
                    setLoadingText("No internet connection. Waiting...");
                    return false;
                }

                const res = await dcmmApi.get(apiRoutes.dcmm.getDeviceDetails, { timeout: 30000 });

                if (res.status === 200 && res.data.success) {
                    setDeviceData(res.data.data as DeviceDetails);
                    setLoadingText("");
                    setAuthError("");
                    console.log(`✅ Device fetched (cycle ${currentCycle + 1}, ${consecutiveFailures} prior failures)`);
                    return true;
                }

                setLoadingText("No response from server");
                return false;
            } catch (error: any) {
                console.error(
                    `❌ Fetch failed (cycle ${currentCycle + 1}):`,
                    error.response?.status || error.code || error.message,
                );

                if (isCorsOrNetworkError(error)) {
                    console.warn("⚠️ Network/CORS error — server unreachable, retrying...");
                    setLoadingText("Waiting for server...");
                    return false;
                }

                if (error.response?.status === 401) {
                    setAuthError(
                        IS_ON_PREM
                            ? "Device IP not recognised. Please contact administrator."
                            : "Authentication failed. Please contact administrator.",
                    );
                    setLoadingText("");
                    return true; // terminal — stop retrying
                }

                // Cloud only: react to offline state
                if (!IS_ON_PREM && !navigator.onLine) {
                    setLoadingText("No internet connection. Waiting...");
                    return false;
                }

                if (!isRetryableError(error)) {
                    setLoadingText("Failed to fetch device data");
                    return true;
                }

                return false;
            }
        };

        while (currentCycle < MAX_CYCLES) {
            currentCycle++;
            console.log(`🔄 Connection cycle ${currentCycle}/${MAX_CYCLES}`);

            for (let attempt = 0; attempt < FAST_RETRY_ATTEMPTS; attempt++) {
                consecutiveFailures++;
                setLoadingText(
                    currentCycle === 1 && attempt === 0 && !isManualRetry
                        ? "Connecting..."
                        : `Connecting... (cycle ${currentCycle}, attempt ${attempt + 1}/${FAST_RETRY_ATTEMPTS})`,
                );

                const success = await attemptFetch();
                if (success) return;

                if (attempt < FAST_RETRY_ATTEMPTS - 1) {
                    await new Promise(r => setTimeout(r, FAST_RETRY_DELAYS[attempt]));
                }
            }

            const breakSeconds = Math.floor(CYCLE_BREAK_DELAY / 1000);
            console.log(`⏸ Cycle ${currentCycle} done — break ${breakSeconds}s`);

            for (let remaining = breakSeconds; remaining > 0; remaining--) {
                // Cloud only: wait for internet to come back
                if (!IS_ON_PREM && !navigator.onLine) {
                    setLoadingText("No internet connection. Waiting...");
                    await new Promise(r => {
                        const id = setInterval(() => {
                            if (navigator.onLine) { clearInterval(id); r(true); }
                        }, 1000);
                    });
                    break;
                }
                setLoadingText(`Connecting... Retrying in ${remaining}s`);
                await new Promise(r => setTimeout(r, 1000));
            }
        }

        console.error(`❌ Failed after ${MAX_CYCLES} cycles`);
        setLoadingText("Unable to connect. Please check your connection.");
    };

    const handleConfigured = () => {
        setIsConfigured(true);
        setAuthError("");
        checkAuthentication();
    };

    // Kept for emergency use — not exposed in UI
    const handleReconfigure = () => {
        if (!window.confirm("Reconfigure this device? Current settings will be lost.")) return;
        DeviceConfigService.clearCredentials();
        setIsConfigured(false);
        setDeviceData(undefined);
        setAuthError("");
        setLoadingText("");
        socketRef.current?.disconnect();
        socketRef.current = null;
        setSocket(null);
    };

    const uploadDeviceScreenshot = async () => {
        try {
            const target = document.getElementById("device-screen-root");
            if (!target) {
                console.error("Screenshot target not found");
                return;
            }

            const canvas = await html2canvas(target, {
                useCORS: true,
                backgroundColor: "#000000",
                scale: 1,
                logging: false,
            });

            const blob: Blob | null = await new Promise((resolve) =>
                canvas.toBlob(resolve, "image/png")
            );

            if (!blob) {
                console.error("Failed to create screenshot blob");
                return;
            }

            const formData = new FormData();
            formData.append("file", blob, `device-screenshot-${Date.now()}.png`);

            await dcmmApi.put("/devices/uploadScreenshot", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            console.log("✅ Screenshot uploaded successfully");
        } catch (error) {
            console.error("❌ Screenshot upload failed:", error);
        }
    };
    // ── Render ────────────────────────────────────────────────────────────────
    const renderLoading = () => (
        <div
            style={{
                height: "100vh", width: "100vw",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexDirection: "column", textAlign: "center",
                backgroundImage: `url(${airplaneWing})`,
                backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat",
            }}
        >
            {loadingText || authError ? (
                <div>
                    <p style={{
                        fontSize: "1.5rem", color: "white",
                        background: authError ? "rgba(200,0,0,0.7)" : "rgba(0,0,0,0.5)",
                        padding: "10px 20px", borderRadius: "8px", marginBottom: "20px",
                    }}>
                        {authError || loadingText}
                    </p>
                    {!authError && loadingText && loadingText !== "Connecting..." && (
                        <button
                            onClick={() => handleSearchDevice(true)}
                            style={{
                                padding: "12px 24px", fontSize: "1rem",
                                color: "white", background: "#48bb78",
                                border: "none", borderRadius: "6px", cursor: "pointer",
                            }}
                        >
                            Retry Now
                        </button>
                    )}
                </div>
            ) : (
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            )}
        </div>
    );

    const renderDisplay = () => {
        if (!deviceData || !deviceData.cluster?.displayType) return renderLoading();
        switch (deviceData.cluster.displayType) {
            case DisplayType.DedicatedGate: return <FlightBoardingPage device={deviceData} />;
            case DisplayType.DedicatedCheckIn: return <FlightCheckInPage device={deviceData} />;
            case DisplayType.DedicatedCarousel: return <BaggageCarouselPage device={deviceData} />;
            case DisplayType.DedicatedCheckInRow: return <CheckInRowPage device={deviceData} />;
            default: return <ConnectDevicePage deviceData={deviceData} />;
        }
    };

    if (isAuthenticating) return renderLoading();

    // On-prem: isConfigured is always true so ConfigureDevicePage never renders
    // Cloud:   isConfigured is false until credentials are stored
    if (!isConfigured) return <ConfigureDevicePage onConfigured={handleConfigured} />;

    return (
        <SocketProvider socket={socket}>
            <div id="device-screen-root" style={{ width: "100vw", height: "100vh" }}>
                <LocateDeviceOverlay
                    open={isLocateVisible}
                    deviceCode={locateDeviceCode}
                    durationMs={5000}
                    onClose={() => {
                        setIsLocateVisible(false);
                        setLocateDeviceCode("");
                    }}
                />
                {isBlackout && (
                    <div style={{
                        position: "fixed",
                        inset: 0,
                        backgroundImage: `url(${mahbBg})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        zIndex: 99999,
                    }}>
                        <p style={{
                            position: "absolute",
                            bottom: "2rem",
                            right: "2rem",
                            color: "#4a4a4a",
                            fontSize: "3rem",
                            fontWeight: 600,
                            letterSpacing: "0.05em",
                            margin: 0,
                        }}>
                            Disconnected
                        </p>
                    </div>
                )}
                {process.env.NODE_ENV === "development" && (
                    <div style={{
                        position: "fixed", top: 10, right: 10,
                        background: "rgba(0,0,0,0.7)", color: "white",
                        padding: "8px", borderRadius: "4px",
                        fontSize: "0.75rem", zIndex: 9999,
                    }}>
                        <div>Mode: {IS_ON_PREM ? "🏢 on-prem" : "☁️ cloud"}</div>
                        <div>Socket: {isSocketConnected ? "🟢" : "🔴"}</div>
                        <div>Online: {IS_ON_PREM ? "N/A" : (isOnline ? "🟢" : "🔴")}</div>
                    </div>
                )}
                {renderDisplay()}
            </div>
        </SocketProvider>
    );
};

export default App;